import { AnfrageDisplayModel, GlobalEnumerationsAceTypphase, MilestoneDisplayModel, MilestoneDisplayModelFormGroup } from '@/api/model-index';
import { AnfrageService } from '@/api/service-index';
import { Constants } from '@/constants';
import { AnfrageNavigationFormComponent, MoveDirection } from '@/core/components/anfrage-navigation-form/anfrage-navigation-form.component';
import { ConfirmationDialogAction, ConfirmationDialogComponent, ConfirmationDialogResult } from '@/core/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ComponentService } from '@/core/services/component.service';
import { I18nService } from '@/core/services/i18nService';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { PortalContextService } from '@/core/services/portal-context.service';
import { SectionService } from '@/core/services/section.service';
import { StateService } from '@/core/services/state.service';
import { TextBoxService } from '@/core/services/textbox.service';
import { UnfinishedHttpMethodsService } from '@/core/services/unfinished-http-methods.service';
import { convertJSDateString } from '@/core/utils/date-converter';
import { Component, inject } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DialogService } from '@progress/kendo-angular-dialog';
import { EditEvent, GridDataResult, RemoveEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'milestones-process-step',
    templateUrl: './milestones-process-step.component.html',
    styleUrls: ['./milestones-process-step.component.scss'],
    standalone: false
})
export class MilestonesProcessStepComponent extends AnfrageNavigationFormComponent {
  public milestones: MilestoneDisplayModel[] = [];
  public milestoneForm: MilestoneDisplayModelFormGroup;
  public anfrage: AnfrageDisplayModel;
  public gridView: GridDataResult;
  public reportId: string;
  public isFormOpen: boolean = false;

  protected componentService = inject(ComponentService)

  public get formGroup(): FormGroup<any> {
    return this.milestoneForm;
  }

  public sort: SortDescriptor[] = [
    { field: "datumPlan", dir: "asc" }
  ];

  public get dateFormat(): string {
    return this.languageService.GetCurrentShortDateFormat(true);
  }

  public get isUpdatedInProjektBericht(): boolean {
    if (this.milestoneForm.Id.value != null) {
      return !((this.milestoneForm.ErstelltInProjektBerichtId.value == null && this.reportId == null) || this.milestoneForm.ErstelltInProjektBerichtId.value == this.reportId);
    }
    return false;
  }

  constructor(
    protected sectionService: SectionService,
    protected route: ActivatedRoute,
    protected loadingAnimationService: LoadingAnimationService,
    protected router: Router,
    protected stateService: StateService,
    protected anfrageService: AnfrageService,
    protected portalContextService: PortalContextService,
    protected unfinishedHttpMethodsService: UnfinishedHttpMethodsService,
    protected languageService: I18nService,
    protected dialogService: DialogService,
    protected textBoxService: TextBoxService
  ) {
    super(sectionService, router, route, portalContextService);
    this.milestoneForm = MilestoneDisplayModelFormGroup.Instance;
    this.anfrage = this.route.snapshot.data.anfrage;
    this.milestones = this.route.snapshot.data.milestones;

    this.reportId = <string>this.route.snapshot.paramMap.get('reportid');
    this.initGridView();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.reportId = this.route.snapshot.paramMap.get('reportid')!;
        this.isFormOpen = false;
      });


    this.sortMilestones();
  }

  protected sortMilestones(): void {
    this.milestones.sort((a, b) => {
      if (!a.datumPlan && !b.datumPlan) return 0;
      if (!a.datumPlan) return 1;
      if (!b.datumPlan) return -1;
      return new Date(a.datumPlan).getTime() - new Date(b.datumPlan).getTime();
    });
  }

  protected initGridView() {
    let sorted = this.milestones.sort((valueA: MilestoneDisplayModel, valueB: MilestoneDisplayModel) => Date.parse(valueA.datumPlan ?? "") - Date.parse(valueB.datumPlan ?? ""));
    this.gridView = {
      data: this.milestones,
      total: this.milestones.length,
    };
  }

  override async canDeactivate(): Promise<boolean> {
    if (!this.milestoneForm.valid && this.isFormOpen) {
      return this.openUnsavedChangesDialog();
    }
    return true;
  }

  protected removeHandler({ dataItem }: RemoveEvent): void {
    let deleteDialogRef = this.dialogService.open({
      content: this.componentService.get(ConfirmationDialogComponent),
      maxWidth: Constants.DIALOG.MAX_WIDTH,
      maxHeight: Constants.DIALOG.MAX_HEIGHT,
      title: this.languageService.instant("schedule-data.dialog.titleDelete")
    });
    let dialogComponent: ConfirmationDialogComponent = deleteDialogRef.content.instance;

    dialogComponent.content = this.languageService.instant("schedule-data.dialog.content");

    deleteDialogRef.result.subscribe(result => this.onDeleteDialogClose(<ConfirmationDialogResult>result, dataItem));
  }

  protected onDeleteDialogClose(result: ConfirmationDialogResult, dataItem: MilestoneDisplayModel) {
    if (result.action == ConfirmationDialogAction.Confirm) {
      const subscription = this.anfrageService.apiAnfrageDeleteMilestoneIdAnfrageIdDelete({ id: dataItem.id!, anfrageId: this.portalContextService.CurrentAnfrage.id, projektberichtId: this.reportId }).subscribe(() => {
        const index = this.milestones.findIndex(item => item.id === dataItem.id);
        if (index !== -1) {
          const removed = this.milestones.splice(index, 1);
          if (this.isFormOpen && removed[0].id === this.milestoneForm.value.id) {
            this.isFormOpen = false;
          }
          this.initGridView();
        }

        this.anfrageService.apiAnfrageAnfrageIdValidateGet({ anfrageId: this.anfrage.id, reportId: this.reportId }).subscribe(result => {
          this.stateService.ValidationErrors = result;
        })
      });

      this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }
  }

  public createHandler() {
    this.openForm(null);
  }

  public editHandler({ dataItem }: EditEvent) {
    this.openForm(dataItem);
  }

  public openForm(milestone: MilestoneDisplayModel | null): void {
    this.milestoneForm = MilestoneDisplayModelFormGroup.Instance;
    this.milestoneForm.Betreff.addValidators(Validators.required);
    this.milestoneForm.DatumPlan.addValidators(Validators.required);

    if (milestone) {
      this.milestoneForm.patchValue(milestone);

      if (milestone.datumPlan != null)
        this.milestoneForm.DatumPlan.patchValue(new Date(milestone.datumPlan));

      if (milestone.datumIst != null)
        this.milestoneForm.DatumIst.patchValue(new Date(milestone.datumIst));
    }
    else {
      if (this.anfrage.phase != null) {
        this.milestoneForm.ErstelltInPhaseId.setValue(this.anfrage.phase.id);

        if (this.anfrage.phase.typ == GlobalEnumerationsAceTypphase.Bericht || this.anfrage.phase.typ == GlobalEnumerationsAceTypphase.Abschluss) {
          this.milestoneForm.ErstelltInProjektBerichtId.setValue(this.reportId);
        }
      }
    }

    this.isFormOpen = true;
  }

  public cancelForm() {
    this.isFormOpen = false;
  }

  public saveMilestone(): void {
    const milestone: MilestoneDisplayModel = this.milestoneForm.value;
    this.isFormOpen = false;

    if (milestone.datumPlan) {
      milestone.datumPlan = convertJSDateString(milestone.datumPlan);
    }
    if (milestone.datumIst) {
      milestone.datumIst = convertJSDateString(milestone.datumIst);
    }

    let subscription = this.anfrageService.apiAnfrageSaveOrUpdateMilestoneAnfrageIdPatch({ anfrageId: this.anfrage.id, projektberichtId: this.reportId, body: milestone }).subscribe((response) => {
      if (response && !milestone.id) {
        milestone.id = response;
        this.milestones.push(milestone);
        this.sortMilestones();
      }
      else {
        const index = this.milestones.findIndex(item => item.id === milestone.id);
        if (index !== -1) {
          this.milestones[index] = milestone;
        }
      }

      this.initGridView();

      this.anfrageService.apiAnfrageAnfrageIdValidateGet({ anfrageId: this.anfrage.id, reportId: this.reportId }).subscribe(result => {
        this.stateService.ValidationErrors = result;
      })
    })

    this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    this.unfinishedHttpMethodsService.addSubscription(subscription);
  }

  protected save(shouldSave: boolean, moveDirection: MoveDirection): void {
    // Nothing to save on that form 
    this.move(moveDirection);

    if (shouldSave && this.milestoneForm.valid && this.isFormOpen) {
      this.saveMilestone();
    }
  }
}